import { MIN_STAMP_WIDTH_PT } from '@dealstack/shared/lib/common/business/stampGeometry';
import { ptToMm } from '@dealstack/shared/lib/common/documentLayout/documentLayoutConstants';

/**
 * The arithmetic behind dragging and resizing a signature anchor, with no DOM in it.
 *
 * Separate from the node view because this is the part that can be wrong in ways nobody sees
 * until a deed is generated: an off-by-a-margin clamp puts a stamp over the page edge, and the
 * box recorded for signing takes the stamp with it. The view owns pointers and pixels; this owns
 * millimetres.
 */

/** How close to a snap point (mm) a drag has to get before it takes it. */
export const SNAP_TOLERANCE_MM = 2.5;

/** The renderer's own legibility floor, in the units the document states. */
export const MIN_WIDTH_MM = ptToMm(MIN_STAMP_WIDTH_PT);

export interface SignatureGeometry {
	widthMm: number;
	offsetXMm: number;
}

export const clampMm = (value: number, min: number, max: number): number =>
	Math.min(Math.max(value, min), Math.max(min, max));

/** Two decimals of a millimetre: fine enough to land on a snap point, short enough to read. */
export const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Flush left, centred, or flush right — the three positions anyone actually wants, offered as
 * snap points rather than as three buttons, so positioning stays one continuous drag.
 */
export const snapOffsetMm = (offsetMm: number, bandWidthMm: number, widthMm: number): number => {
	const room = Math.max(0, bandWidthMm - widthMm);
	const targets = [0, room / 2, room];
	const nearest = targets.reduce((best, target) =>
		Math.abs(target - offsetMm) < Math.abs(best - offsetMm) ? target : best
	);
	return Math.abs(nearest - offsetMm) <= SNAP_TOLERANCE_MM ? nearest : offsetMm;
};

/**
 * Where a move gesture leaves the anchor. Bounded by the band it sits in, so a stamp cannot be
 * dragged over the margin however far the pointer goes.
 */
export const movedGeometry = (
	start: SignatureGeometry,
	deltaMm: number,
	bandWidthMm: number
): SignatureGeometry => {
	const room = Math.max(0, bandWidthMm - start.widthMm);
	return {
		widthMm: start.widthMm,
		offsetXMm: snapOffsetMm(
			clampMm(start.offsetXMm + deltaMm, 0, room),
			bandWidthMm,
			start.widthMm
		),
	};
};

/**
 * Where a resize gesture leaves the anchor.
 *
 * Anchored by its LEFT edge, so resizing never also moves it — a handle that did both is the
 * one thing that makes a placement editor feel broken. The ceiling is what is left of the band
 * to the right of where it already sits, which is why the offset is an input here.
 */
export const resizedGeometry = (
	start: SignatureGeometry,
	deltaMm: number,
	bandWidthMm: number
): SignatureGeometry => ({
	widthMm: clampMm(
		start.widthMm + deltaMm,
		MIN_WIDTH_MM,
		Math.max(MIN_WIDTH_MM, bandWidthMm - start.offsetXMm)
	),
	offsetXMm: start.offsetXMm,
});

/**
 * The geometry as ATTRIBUTES, which is not the same shape.
 *
 * The design size and flush-left are written as absent rather than as numbers: they are what a
 * document that has never been touched already means, so storing them would make an untouched
 * anchor differ from itself depending on whether anyone had ever clicked it.
 */
export const geometryAttributes = (
	geometry: SignatureGeometry,
	designWidthMm: number
): { widthMm: number | null; offsetXMm: number | null } => ({
	widthMm: Math.abs(geometry.widthMm - designWidthMm) < 0.01 ? null : round2(geometry.widthMm),
	offsetXMm: geometry.offsetXMm < 0.01 ? null : round2(geometry.offsetXMm),
});
